// ============================================
// TOKEN USAGE DISPLAY COMPONENT
// Shows user's current token usage with real-time updates
// ============================================

import React, { useState, useEffect } from 'react';
import { useAuth } from './context/AuthContext';

const WORKER_URL = 'https://codemine-api-worker.shazim-rv11-675.workers.dev';

interface TokenUsage {
  daily: {
    used: number;
    limit: number;
    remaining: number;
    percentage: number;
  };
  monthly: {
    used: number;
    limit: number;
    remaining: number;
    percentage: number;
  };
  lifetime: {
    used: number;
  };
  plan: string;
  costs: {
    start: number;
    continue: number;
  };
}

interface TokenUsageDisplayProps {
  isDarkMode?: boolean;
  compact?: boolean;
  onRefresh?: () => void;
}

export function TokenUsageDisplay({ isDarkMode = true, compact = false, onRefresh }: TokenUsageDisplayProps) {
  const { user } = useAuth();
  const [usage, setUsage] = useState<TokenUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  // Fetch token usage
  const fetchUsage = async () => {
    if (!user?.email) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${WORKER_URL}/users/tokens/usage?userId=${encodeURIComponent(user.email)}`);
      const data = await response.json() as { error?: string; success?: boolean; usage?: TokenUsage };

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch token usage');
      }

      if (data.success && data.usage) {
        setUsage(data.usage);
      }
    } catch (err) {
      console.error('Token usage fetch error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load token usage');
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount and when user changes
  useEffect(() => {
    fetchUsage();
  }, [user]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchUsage, 30000);
    return () => clearInterval(interval);
  }, [user]);

  // Call onRefresh when data updates
  useEffect(() => {
    if (onRefresh && usage) {
      onRefresh();
    }
  }, [usage, onRefresh]);

  if (!user) {
    return null;
  }

  if (loading && !usage) {
    return (
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
        isDarkMode
          ? 'bg-slate-800/50 border border-white/10'
          : 'bg-gray-100 border border-gray-300'
      }`}>
        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Loading...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer ${
        isDarkMode
          ? 'bg-red-900/20 border border-red-500/30 hover:bg-red-900/30'
          : 'bg-red-100 border border-red-300 hover:bg-red-200'
      }`} onClick={fetchUsage}>
        <span className="text-red-500">⚠️</span>
        <span className={`text-xs font-medium ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
          Click to retry
        </span>
      </div>
    );
  }

  if (!usage) {
    return null;
  }

  // Determine warning level
  const dailyPercentage = usage.daily.percentage;
  const monthlyPercentage = usage.monthly.percentage;
  const isWarning = dailyPercentage >= 70 || monthlyPercentage >= 70;
  const isCritical = dailyPercentage >= 90 || monthlyPercentage >= 90;

  // Compact view for header - Subtle and Premium
  if (compact) {
    return (
      <div className="relative">
        <button
          onClick={() => setShowDetails(!showDetails)}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg backdrop-blur-sm transition-all border ${
            isDarkMode
              ? 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/15'
              : 'bg-gray-900/5 border-gray-300/30 hover:bg-gray-900/10 hover:border-gray-400/40'
          }`}
        >
          <div className="flex items-center gap-2">
            <span className={`text-[10px] font-medium uppercase tracking-widest ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Tokens
            </span>
            <div className={`h-3 w-px ${isDarkMode ? 'bg-white/20' : 'bg-gray-300'}`}></div>
            <span className={`text-xs font-semibold tabular-nums ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              {usage.daily.used.toLocaleString()}
            </span>
            <span className={`text-[10px] font-medium ${
              isDarkMode ? 'text-gray-500' : 'text-gray-500'
            }`}>
              / {usage.daily.limit.toLocaleString()}
            </span>
          </div>
        </button>

        {/* Dropdown Details */}
        {showDetails && (
          <div className={`absolute top-full right-0 mt-2 w-80 rounded-xl shadow-2xl border z-50 ${
            isDarkMode
              ? 'bg-slate-900 border-white/10'
              : 'bg-white border-gray-200'
          }`}>
            <div className={`px-4 py-3 border-b ${
              isDarkMode ? 'border-white/10' : 'border-gray-200'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className={`text-sm font-black ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Token Usage
                </h3>
                <button
                  onClick={fetchUsage}
                  className={`p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>
              <div className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Plan: <span className={`${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>{usage.plan}</span>
              </div>
            </div>

            <div className="p-4 space-y-4">
              {/* Daily Usage */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Daily Usage
                  </span>
                  <span className={`text-xs font-black ${
                    usage.daily.percentage >= 90
                      ? isDarkMode ? 'text-red-400' : 'text-red-600'
                      : usage.daily.percentage >= 70
                        ? isDarkMode ? 'text-amber-400' : 'text-amber-600'
                        : isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
                  }`}>
                    {usage.daily.used} / {usage.daily.limit}
                  </span>
                </div>
                <div className={`w-full h-2 rounded-full overflow-hidden ${
                  isDarkMode ? 'bg-gray-800' : 'bg-gray-200'
                }`}>
                  <div
                    className={`h-full transition-all rounded-full ${
                      usage.daily.percentage >= 90
                        ? 'bg-gradient-to-r from-red-500 to-red-600'
                        : usage.daily.percentage >= 70
                          ? 'bg-gradient-to-r from-amber-500 to-amber-600'
                          : 'bg-gradient-to-r from-emerald-500 to-emerald-600'
                    }`}
                    style={{ width: `${Math.min(usage.daily.percentage, 100)}%` }}
                  />
                </div>
                <div className={`text-[10px] font-medium mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  {usage.daily.remaining} tokens remaining today
                </div>
              </div>

              {/* Monthly Usage */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Monthly Usage
                  </span>
                  <span className={`text-xs font-black ${
                    usage.monthly.percentage >= 90
                      ? isDarkMode ? 'text-red-400' : 'text-red-600'
                      : usage.monthly.percentage >= 70
                        ? isDarkMode ? 'text-amber-400' : 'text-amber-600'
                        : isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
                  }`}>
                    {usage.monthly.used} / {usage.monthly.limit}
                  </span>
                </div>
                <div className={`w-full h-2 rounded-full overflow-hidden ${
                  isDarkMode ? 'bg-gray-800' : 'bg-gray-200'
                }`}>
                  <div
                    className={`h-full transition-all rounded-full ${
                      usage.monthly.percentage >= 90
                        ? 'bg-gradient-to-r from-red-500 to-red-600'
                        : usage.monthly.percentage >= 70
                          ? 'bg-gradient-to-r from-amber-500 to-amber-600'
                          : 'bg-gradient-to-r from-emerald-500 to-emerald-600'
                    }`}
                    style={{ width: `${Math.min(usage.monthly.percentage, 100)}%` }}
                  />
                </div>
                <div className={`text-[10px] font-medium mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  {usage.monthly.remaining} tokens remaining this month
                </div>
              </div>

              {/* Lifetime Usage */}
              <div className={`pt-3 border-t ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Total Used (Lifetime)
                  </span>
                  <span className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {usage.lifetime.used.toLocaleString()} tokens
                  </span>
                </div>
              </div>

              {/* Upgrade CTA if close to limit */}
              {(isWarning || isCritical) && (
                <div className={`p-3 rounded-lg border ${
                  isCritical
                    ? isDarkMode
                      ? 'bg-red-900/20 border-red-500/30'
                      : 'bg-red-50 border-red-200'
                    : isDarkMode
                      ? 'bg-amber-900/20 border-amber-500/30'
                      : 'bg-amber-50 border-amber-200'
                }`}>
                  <div className={`text-xs font-bold mb-1 ${
                    isCritical
                      ? isDarkMode ? 'text-red-300' : 'text-red-700'
                      : isDarkMode ? 'text-amber-300' : 'text-amber-700'
                  }`}>
                    {isCritical ? '⚠️ Token Limit Reached!' : '⚡ Running Low on Tokens'}
                  </div>
                  <div className={`text-[10px] mb-2 ${
                    isCritical
                      ? isDarkMode ? 'text-red-200' : 'text-red-600'
                      : isDarkMode ? 'text-amber-200' : 'text-amber-600'
                  }`}>
                    Upgrade your plan for more tokens and faster generation
                  </div>
                  <button
                    onClick={() => window.location.href = '/#pricing'}
                    className={`w-full px-3 py-1.5 rounded-lg text-white text-xs font-bold transition-all ${
                      isCritical
                        ? 'bg-gradient-to-r from-red-500 to-red-600 hover:shadow-lg'
                        : 'bg-gradient-to-r from-amber-500 to-amber-600 hover:shadow-lg'
                    }`}
                  >
                    Upgrade Plan
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Full view for dashboard/settings
  return (
    <div className={`p-6 rounded-2xl border ${
      isDarkMode
        ? 'bg-slate-900 border-white/10'
        : 'bg-white border-gray-200'
    }`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className={`text-xl font-black ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Token Usage
          </h2>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Plan: <span className={`font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>{usage.plan}</span>
          </p>
        </div>
        <button
          onClick={fetchUsage}
          className={`p-2 rounded-lg transition-colors ${
            isDarkMode
              ? 'hover:bg-slate-800'
              : 'hover:bg-gray-100'
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* Rest of full view similar to above... */}
      {/* (Abbreviated for brevity - same structure as compact dropdown) */}
    </div>
  );
}

export default TokenUsageDisplay;
